/**
 * Export formats — Phase 25-AB Task 12.
 *
 * Source-of-truth: docs/mockups/export.html lines 269-541 (panel 20).
 * 6 categories × 26 total formats. The mockup header reads "총 24 형식"
 * because two ZIP bundles (MML / CapCut Folder) historically counted as
 * one "통합 패키지" entry — we keep all 26 here for explicit toggling.
 */

export type FormatCategory =
	| "video"
	| "nle"
	| "subtitle"
	| "audio"
	| "visual"
	| "bundle";

export type FormatCost = "free" | "1C" | "2C" | "3C";
export type FormatTag = "popular" | "pro" | "new" | "mml" | null;

export interface ExportFormat {
	/** stable id used as React key + onToggle param */
	key: string;
	category: FormatCategory;
	label: string;
	ext: string;
	description: string;
	cost: FormatCost;
	tag: FormatTag;
	/** mockup data-dev id (verifies parity with export.html) */
	dev: string;
}

export const CATEGORY_LABELS: Record<FormatCategory, string> = {
	video: "A · 영상 출력",
	nle: "B · NLE 프로젝트 (편집 계속)",
	subtitle: "C · 자막 · 스크립트",
	audio: "D · 오디오",
	visual: "E · 시각 자료",
	bundle: "F · 통합 패키지",
};

export const FORMATS: readonly ExportFormat[] = [
	// A · Video
	{
		key: "mp4-1080p",
		category: "video",
		label: "MP4 1080p",
		ext: ".mp4 · H.264",
		description:
			"표준 화질 — YouTube/TikTok/Instagram 모두 호환. 9:16 세로 1080×1920.",
		cost: "free",
		tag: "popular",
		dev: "20-B1",
	},
	{
		key: "mp4-720p",
		category: "video",
		label: "MP4 720p",
		ext: ".mp4 · H.264",
		description: "저용량 — 빠른 업로드, 모바일 전용. 720×1280.",
		cost: "free",
		tag: null,
		dev: "20-B2",
	},
	{
		key: "mp4-4k",
		category: "video",
		label: "MP4 4K",
		ext: ".mp4 · H.265",
		description:
			"초고화질 — Pro 권장. 2160×3840 + HEVC. 렌더 시간 ~3분/클립.",
		cost: "2C",
		tag: "pro",
		dev: "20-B3",
	},
	{
		key: "gif",
		category: "video",
		label: "GIF 1초",
		ext: ".gif · animated",
		description: "미리보기/공유용 짧은 GIF — 클립 시작 1초.",
		cost: "free",
		tag: null,
		dev: "20-B4",
	},

	// B · NLE
	{
		key: "capcut-draft",
		category: "nle",
		label: "CapCut .draft",
		ext: ".draft · folder",
		description:
			"CapCut Desktop / Mobile / Web 모두 호환. 자동 import 폴더에 저장하면 다음 실행 시 프로젝트 즉시 표시.",
		cost: "1C",
		tag: "popular",
		dev: "20-C1",
	},
	{
		key: "fcpxml",
		category: "nle",
		label: "FCPXML 1.10",
		ext: ".fcpxml",
		description: "Final Cut Pro / DaVinci Resolve. 표준 open format.",
		cost: "1C",
		tag: null,
		dev: "20-C2",
	},
	{
		key: "premiere-xml",
		category: "nle",
		label: "Premiere XML",
		ext: ".xml · prproj",
		description:
			"Adobe Premiere Pro 직접 import. 클립·자막·전환 모두 보존.",
		cost: "1C",
		tag: null,
		dev: "20-C3",
	},
	{
		key: "davinci-drp",
		category: "nle",
		label: "DaVinci .drp",
		ext: ".drp",
		description: "DaVinci Resolve — 전문 색보정 워크플로우.",
		cost: "1C",
		tag: null,
		dev: "20-C4",
	},
	{
		key: "edl",
		category: "nle",
		label: "EDL",
		ext: ".edl · CMX3600",
		description: "Edit Decision List — 가장 universal한 NLE 교환 형식.",
		cost: "free",
		tag: null,
		dev: "20-C5",
	},
	{
		key: "shotcut-mlt",
		category: "nle",
		label: "Shotcut MLT",
		ext: ".mlt",
		description: "오픈소스 Shotcut — 무료 사용자에 추천.",
		cost: "free",
		tag: null,
		dev: "20-C6",
	},
	{
		key: "otio",
		category: "nle",
		label: "OpenTimelineIO",
		ext: ".otio",
		description: "Pixar 표준 — 모든 NLE 상호 변환 가능.",
		cost: "1C",
		tag: "new",
		dev: "20-C7",
	},

	// C · Subtitle
	{
		key: "srt",
		category: "subtitle",
		label: "SRT",
		ext: ".srt",
		description: "YouTube / 모든 플레이어 호환 표준 자막.",
		cost: "free",
		tag: "popular",
		dev: "20-D1",
	},
	{
		key: "vtt",
		category: "subtitle",
		label: "WebVTT",
		ext: ".vtt",
		description: "HTML5 video / YouTube 자막.",
		cost: "free",
		tag: null,
		dev: "20-D2",
	},
	{
		key: "ass",
		category: "subtitle",
		label: "ASS / SSA",
		ext: ".ass",
		description: "libass — 폰트/색/위치/애니메이션 스타일 보존.",
		cost: "free",
		tag: null,
		dev: "20-D3",
	},
	{
		key: "txt",
		category: "subtitle",
		label: "TXT 스크립트",
		ext: ".txt",
		description: "Whisper transcript plain text — 검수/번역용.",
		cost: "free",
		tag: null,
		dev: "20-D4",
	},
	{
		key: "json",
		category: "subtitle",
		label: "JSON 스크립트",
		ext: ".json",
		description: "word-level timestamps + confidence + speaker labels.",
		cost: "free",
		tag: null,
		dev: "20-D5",
	},
	{
		key: "csv",
		category: "subtitle",
		label: "CSV 메타데이터",
		ext: ".csv",
		description:
			"제목 / 해시태그 / CTA / 카테고리 / 후킹 점수 — 일괄 업로드용.",
		cost: "free",
		tag: null,
		dev: "20-D6",
	},

	// D · Audio
	{
		key: "tts-mp3",
		category: "audio",
		label: "TTS Narration",
		ext: ".mp3 · 192k",
		description:
			"합성된 voice 음성 (ElevenLabs / Edge TTS). 클립별 분리.",
		cost: "free",
		tag: "mml",
		dev: "20-E1",
	},
	{
		key: "bgm-mp3",
		category: "audio",
		label: "BGM",
		ext: ".mp3 · 192k",
		description: "선택한 BGM (라이선스 정보 .txt 동봉).",
		cost: "free",
		tag: null,
		dev: "20-E2",
	},
	{
		key: "wav-orig",
		category: "audio",
		label: "원본 오디오",
		ext: ".wav · 44.1k",
		description: "영상에서 분리한 오디오 — Whisper 입력/추가 편집용.",
		cost: "free",
		tag: null,
		dev: "20-E3",
	},

	// E · Visual
	{
		key: "thumbnails",
		category: "visual",
		label: "썸네일 6장",
		ext: ".jpg · 1920×1080",
		description: "AI 추천 6장 (ffmpeg + Claude scoring).",
		cost: "1C",
		tag: "mml",
		dev: "20-F1",
	},
	{
		key: "png-sequence",
		category: "visual",
		label: "PNG sequence",
		ext: ".png × N",
		description: "모든 프레임을 PNG sequence로 — 추가 편집/모션 디자인.",
		cost: "1C",
		tag: null,
		dev: "20-F2",
	},
	{
		key: "mobile-preview",
		category: "visual",
		label: "Mobile 미리보기",
		ext: ".mp4 · 9:16",
		description: "모바일 화면 시뮬레이션 미리보기 영상.",
		cost: "free",
		tag: null,
		dev: "20-F3",
	},

	// F · Bundle
	{
		key: "mml-project",
		category: "bundle",
		label: "MML Project",
		ext: ".mml.zip",
		description:
			"우리 자체 형식 — 모든 편집 상태 100% 복원 가능. Re-import 지원.",
		cost: "2C",
		tag: "mml",
		dev: "20-G1",
	},
	{
		key: "capcut-folder",
		category: "bundle",
		label: "CapCut Folder",
		ext: ".zip",
		description:
			"CapCut에서 그대로 import 가능한 폴더 zip. 자동 import 폴더로 직접 복사 옵션.",
		cost: "1C",
		tag: "popular",
		dev: "20-G2",
	},
	{
		key: "all-in-one",
		category: "bundle",
		label: "All-in-One",
		ext: ".zip",
		description:
			"위 모든 형식을 한 번에 묶어 다운로드 (선택된 항목 모두).",
		cost: "3C",
		tag: "mml",
		dev: "20-G3",
	},
] as const;

export const FORMAT_COUNT = FORMATS.length;

export const CATEGORIES: readonly FormatCategory[] = [
	"video",
	"nle",
	"subtitle",
	"audio",
	"visual",
	"bundle",
];

/** Group formats by category, preserving declaration order. */
export function formatsByCategory(): Record<FormatCategory, ExportFormat[]> {
	const out = {
		video: [] as ExportFormat[],
		nle: [] as ExportFormat[],
		subtitle: [] as ExportFormat[],
		audio: [] as ExportFormat[],
		visual: [] as ExportFormat[],
		bundle: [] as ExportFormat[],
	};
	for (const f of FORMATS) out[f.category].push(f);
	return out;
}

/** Coin cost for a format (numeric for summing). */
export function formatCoinCost(cost: FormatCost): number {
	if (cost === "free") return 0;
	if (cost === "1C") return 1;
	if (cost === "2C") return 2;
	return 3;
}

/** Default selected on first mount (matches mockup "6 선택됨"). */
export const DEFAULT_SELECTED: readonly string[] = [
	"mp4-1080p",
	"capcut-draft",
	"srt",
	"txt",
	"capcut-folder",
	"thumbnails",
];
